import { useEffect, useState } from "react";
import { useToast } from "../ui/Toast";
import api from "../../services/api";
import { trackEvent } from "../../utils/analytics";

const Brokers = () => {
  const toast = useToast();
  const [brokerAccounts, setBrokerAccounts] = useState([]);
  const [supportedBrokers, setSupportedBrokers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [formData, setFormData] = useState({});
  const [testingConnection, setTestingConnection] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState({});
  const [syncDates, setSyncDates] = useState({});

  useEffect(() => {
    fetchBrokerAccounts();
    fetchSupportedBrokers();

    // Listen for OAuth success messages
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === "SCHWAB_OAUTH_SUCCESS" || event.data.type === "BROKER_OAUTH_SUCCESS") {
        console.log(
          "Brokers component: OAuth success received, refreshing data..."
        );
        fetchBrokerAccounts();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const fetchSupportedBrokers = async () => {
    try {
      const { data } = await api.get("/broker-accounts/supported");
      if (data.success) {
        setSupportedBrokers(data.data.brokers);
      }
    } catch (error) {
      console.error("Failed to fetch supported brokers:", error);
    }
  };

  const fetchBrokerAccounts = async () => {
    try {
      console.log("🔄 Fetching broker accounts...");
      const { data } = await api.get("/broker-accounts");
      console.log("📦 Broker accounts response:", data);
      if (data.success) {
        console.log("✅ Setting broker accounts:", data.data.brokerAccounts);
        setBrokerAccounts(data.data.brokerAccounts);
      } else {
        console.log("❌ Response not successful:", data);
      }
    } catch (error) {
      console.error("❌ Failed to fetch broker accounts:", error);
    } finally {
      setLoading(false);
    }
  };

  const createAccountAndAuthenticate = async (broker, credentials) => {
    // Check if account already exists
    const existingAccount = brokerAccounts.find(
      (acc) => acc.brokerName === broker.id
    );

    if (existingAccount) {
      console.log("Account already exists, proceeding to auth...", existingAccount);
      if (broker.authType === "oauth") {
        setShowModal(false);
        await handleOAuthFlow(existingAccount.id);
      } else {
        setShowModal(false);
        toast.info("Broker account is already connected!");
      }
      return;
    }
    try {
      const { data } = await api.post("/broker-accounts", {
        brokerName: broker.id,
        brokerDisplayName: broker.name,
        accountId: credentials.accountId || "main",
        accountName: `${broker.name} Account`,
        apiCredentials: credentials,
        isPaper: credentials.baseUrl === "https://paper-api.alpaca.markets",
      });

      if (data.success) {
        const accountId = data.data.brokerAccount.id;

        if (broker.authType === "oauth") {
          setShowModal(false);
          await handleOAuthFlow(accountId);
        } else {
          setShowModal(false);
          fetchBrokerAccounts();
          trackEvent("broker_connected", { broker: broker.id, auth_type: "api_key" });
          toast.success("Broker account added successfully!");
        }
      }
    } catch (error) {
      // If error is 400 (duplicate), try to recover
      if (error.response && error.response.status === 400) {
         toast.warning("Broker account already exists. Please delete the existing account or authorize it directly.");
      } else {
         toast.error(`Failed to add broker account: ${error.message}`);
      }
    }
  };

  const handleBrokerSelect = (broker) => {
    setSelectedBroker(broker);

    // Check if broker has no fields (managed credentials) OR is Schwab (explicit override)
    if (broker.id === "schwab" || !broker.fields || broker.fields.length === 0) {
      // For Schwab, always generate a unique temporary ID to allow multiple accounts
      // The backend will resolve the REAL account ID after login and merge/update as needed.
      const tempId = `new_${Date.now()}`;
      console.log("Generating temporary ID for Schwab flow:", tempId);
      
      createAccountAndAuthenticate(broker, { accountId: tempId });
      return;
    }

    if (broker.authType === "oauth") {
      // For OAuth brokers, create account first with basic credentials
      const initialFormData = {};
      broker.fields.forEach((field) => {
        initialFormData[field.name] = "";
      });
      setFormData(initialFormData);
      setShowModal(true);
    } else {
      // For API key brokers, show credential form
      const initialFormData = {};
      broker.fields.forEach((field) => {
        initialFormData[field.name] =
          field.type === "select" ? field.options[0].value : "";
      });
      setFormData(initialFormData);
      setShowModal(true);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const { data } = await api.post("/broker-accounts/test-connection", {
        brokerType: selectedBroker.id,
        credentials: formData,
      });

      if (data.success && data.data.success) {
        toast.success("Connection test successful!");
      } else {
        toast.error(`Connection test failed: ${data.data.error}`);
      }
    } catch (error) {
      toast.error(`Connection test failed: ${error.message}`);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await createAccountAndAuthenticate(selectedBroker, formData);
  };

  const handleSync = async (accountId, syncType = "trades") => {
    setSyncing((prev) => ({ ...prev, [accountId]: syncType }));

    try {
      const dates = syncDates[accountId] || {};
      const payload = {
        syncType,
      };

      // Add dates if specified
      if (dates.startDate) {
        payload.startDate = new Date(dates.startDate).toISOString();
      }
      if (dates.endDate) {
        payload.endDate = new Date(dates.endDate).toISOString();
      }

      const { data } = await api.post(`/broker-accounts/${accountId}/sync`, payload);

      if (data.success) {
        trackEvent("trade_sync_started", { account_id: accountId });
        toast.success("Sync started successfully! Check back in a few moments.");

        // Poll for sync status
        setTimeout(() => {
          fetchBrokerAccounts();
          setSyncing((prev) => {
            const newSyncing = { ...prev };
            delete newSyncing[accountId];
            return newSyncing;
          });
        }, 5000);
      }
    } catch (error) {
      toast.error(`Sync failed: ${error.message}`);
      setSyncing((prev) => {
        const newSyncing = { ...prev };
        delete newSyncing[accountId];
        return newSyncing;
      });
    }
  };

  const setQuickDateRange = (accountId, range) => {
    const endDate = new Date().toISOString().split("T")[0];
    let startDate;

    switch (range) {
      case "30d":
        startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0];
        break;
      case "3m":
        startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0];
        break;
      case "1y":
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        startDate = oneYearAgo.toISOString().split("T")[0];
        break;
      case "all":
        const tenYearsAgo = new Date();
        tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
        startDate = tenYearsAgo.toISOString().split("T")[0];
        break;
      default:
        startDate = endDate;
    }

    setSyncDates((prev) => ({
      ...prev,
      [accountId]: { startDate, endDate },
    }));
  };

  const handleDeleteAccount = async (accountId, brokerName) => {
    if (
      !confirm(
        `Are you sure you want to delete the ${brokerName} account? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      const { data } = await api.delete(`/broker-accounts/${accountId}`);
      if (data.success) {
        toast.success("Broker account deleted successfully!");
        fetchBrokerAccounts();
      }
    } catch (error) {
      toast.error(`Failed to delete broker account: ${error.message}`);
    }
  };

  const handleOAuthFlow = async (accountId) => {
    try {
      // Determine endpoint based on selected broker or account
      // If we have selectedBroker (add flow), use its ID
      // If we are validating an existing account, use account.brokerName (or similar)
      // Since function argument is only accountId, and component state 'selectedBroker' might be cleared or irrelevant for existing account re-auth,
      // we need a robust way.
      // Lookup account to find broker type
      const account = brokerAccounts.find(a => a.id === accountId);
      const brokerId = account ? account.brokerName : selectedBroker?.id;
      
      const endpoint = "/broker-accounts/schwab/auth-url";

      const { data } = await api.post(endpoint, {
        brokerAccountId: accountId,
      });

      if (data.success) {
        const { authUrl } = data.data;

        // Open OAuth popup
        const popup = window.open(
          authUrl,
          "oauth-popup",
          "width=600,height=700,scrollbars=yes,resizable=yes"
        );

        if (!popup) {
          toast.warning("Popup blocked! Please allow popups for this site.");
          return;
        }

        // Listen for popup messages
        const messageListener = (event) => {
          // Verify origin for security
          if (event.origin !== window.location.origin) return;

          if (event.data.type === "SCHWAB_OAUTH_SUCCESS" || event.data.type === "BROKER_OAUTH_SUCCESS") {
            popup.close();
            window.removeEventListener("message", messageListener);
            trackEvent("broker_connected", { broker: event.data.broker || "oauth", auth_type: "oauth" });
            toast.success("Authorization successful! You can now sync your account.");
            fetchBrokerAccounts();
          } else if (event.data.type === "SCHWAB_OAUTH_ERROR" || event.data.type === "BROKER_OAUTH_ERROR") {
            popup.close();
            window.removeEventListener("message", messageListener);
            toast.error(`Authorization failed: ${event.data.error}`);
          }
        };

        window.addEventListener("message", messageListener);

        // Poll for status updates as backup (in case message doesn't come through)
        const pollForStatusUpdate = async () => {
          try {
            const { data: statusData } = await api.get("/broker-accounts");
            if (statusData.success) {
              const account = statusData.data.brokerAccounts.find(
                (acc) => acc.id === accountId
              );
              if (account && account.syncStatus === "ready") {
                // Status has been updated to ready
                popup.close();
                window.removeEventListener("message", messageListener);
                toast.success("Authorization successful! You can now sync your account.");
                fetchBrokerAccounts();
                return true; // Stop polling
              }
            }
          } catch (error) {
            console.error("Error polling for status update:", error);
          }
          return false;
        };

        // Poll every 3 seconds for up to 2 minutes
        let pollCount = 0;
        const maxPolls = 40; // 40 * 3 seconds = 2 minutes

        const pollInterval = setInterval(async () => {
          pollCount++;
          const statusUpdated = await pollForStatusUpdate();

          if (statusUpdated || pollCount >= maxPolls || popup.closed) {
            clearInterval(pollInterval);
            window.removeEventListener("message", messageListener);
          }
        }, 3000);

        // Check if popup is closed without completing auth
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            clearInterval(pollInterval);
            window.removeEventListener("message", messageListener);
            // Always refresh broker accounts when popup closes
            console.log("🔄 Popup closed, refreshing broker accounts...");
            fetchBrokerAccounts();
          }
        }, 1000);
      }
    } catch (error) {
      toast.error(`Failed to start OAuth flow: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-slate-400">Loading brokers...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Broker Accounts</h1>
          <p className="text-slate-400">Manage your connected trading accounts</p>
        </div>
        <button 
          onClick={() => setShowModal(true)} 
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors"
        >
          <div className="text-xl">➕</div>
          Add Broker
        </button>
      </div>

      {/* Existing Broker Accounts */}
      <div className="bg-slate-800/50 backdrop-blur-md border border-slate-700 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white mb-6">Connected Accounts</h2>

        {brokerAccounts.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-4">🔗</div>
            <p className="text-slate-400 text-lg">
              No broker accounts connected yet. Add your first broker to start syncing trades!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {brokerAccounts.map((account) => (
              <div
                key={account.id}
                className={`bg-slate-800 border-l-4 rounded-xl p-6 shadow-sm ${
                  account.syncStatus === "success"
                    ? "border-l-emerald-500"
                    : account.syncStatus === "error"
                    ? "border-l-red-500"
                    : "border-l-amber-500"
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="text-lg font-bold text-white mb-1">
                      {account.brokerDisplayName}
                    </div>
                    <div className="text-sm text-slate-400">
                      {account.accountName} •{" "}
                      {account.isPaper ? "Paper Trading" : "Live Trading"}
                    </div>
                  </div>
                  <div
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      account.syncStatus === "success"
                        ? "bg-emerald-500/10 text-emerald-400"
                        : account.syncStatus === "error"
                        ? "bg-red-500/10 text-red-400"
                        : "bg-amber-500/10 text-amber-400"
                    }`}
                  >
                    {account.syncStatus === "syncing"
                      ? "Syncing..."
                      : account.syncStatus === "success"
                      ? "Connected"
                      : account.syncStatus === "ready"
                      ? "Ready"
                      : account.syncStatus === "error"
                      ? "Error"
                      : "Pending"}
                  </div>
                </div>

                {/* Date Range Selector */}
                <div className="mt-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-semibold text-slate-300">
                      Sync Date Range
                    </span>
                    <button
                      onClick={() =>
                        setShowDatePicker((prev) => ({
                          ...prev,
                          [account.id]: !prev[account.id],
                        }))
                      }
                      className="text-xs text-indigo-400 hover:text-indigo-300 underline bg-transparent border-0 cursor-pointer"
                    >
                      {showDatePicker[account.id] ? "Hide" : "Custom Dates"}
                    </button>
                  </div>

                  {/* Quick Actions */}
                  <div className={`flex flex-wrap gap-2 ${showDatePicker[account.id] ? "mb-3" : ""}`}>
                    {["30d", "3m", "1y", "all"].map((range) => (
                      <button
                        key={range}
                        onClick={() => setQuickDateRange(account.id, range)}
                        className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded transition-colors"
                      >
                        {range === "30d" ? "Last 30 Days" : range === "3m" ? "Last 3 Months" : range === "1y" ? "Last Year" : "All History"}
                      </button>
                    ))}
                  </div>

                  {/* Custom Date Picker */}
                  {showDatePicker[account.id] && (
                    <div className="flex gap-4 items-center">
                      <div className="flex-1">
                        <label className="block text-xs text-slate-400 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={syncDates[account.id]?.startDate || ""}
                          onChange={(e) =>
                            setSyncDates((prev) => ({
                              ...prev,
                              [account.id]: {
                                ...prev[account.id],
                                startDate: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-3 py-1.5 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:outline-none focus:border-indigo-500"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs text-slate-400 mb-1">End Date</label>
                        <input
                          type="date"
                          value={syncDates[account.id]?.endDate || ""}
                          onChange={(e) =>
                            setSyncDates((prev) => ({
                              ...prev,
                              [account.id]: {
                                ...prev[account.id],
                                endDate: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-3 py-1.5 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:outline-none focus:border-indigo-500"
                        />
                      </div>
                    </div>
                  )}

                  {/* Display selected range */}
                  {syncDates[account.id]?.startDate && (
                    <div className="text-xs text-emerald-400 mt-2 font-medium">
                      Will sync from {syncDates[account.id].startDate} to{" "}
                      {syncDates[account.id].endDate || "today"}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-3 mt-4">
                  {account.brokerName === "schwab" &&
                    account.syncStatus !== "ready" && (
                      <button
                        onClick={() => handleOAuthFlow(account.id)}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        Authorize Access
                      </button>
                    )}

                  {["trades", "positions", "account"].map((type) => (
                    <button
                      key={type}
                      onClick={() => handleSync(account.id, type)}
                      disabled={
                        syncing[account.id] ||
                        account.syncStatus === "pending" ||
                        account.syncStatus === "error"
                      }
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        type === "trades"
                         ? "bg-indigo-600 hover:bg-indigo-700 text-white" 
                         : "bg-slate-700 hover:bg-slate-600 text-slate-200"
                      } ${
                        (syncing[account.id] || account.syncStatus === "pending" || account.syncStatus === "error") 
                        ? "opacity-50 cursor-not-allowed" 
                        : ""
                      }`}
                    >
                      {syncing[account.id] === type
                        ? `Syncing ${type.charAt(0).toUpperCase() + type.slice(1)}...`
                        : `Sync ${type.charAt(0).toUpperCase() + type.slice(1)}`}
                    </button>
                  ))}

                  <button
                    onClick={() =>
                      handleDeleteAccount(account.id, account.brokerDisplayName)
                    }
                    className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-sm font-medium transition-colors"
                  >
                    Delete
                  </button>
                </div>

                {account.lastSync && (
                  <div className="text-xs text-slate-500 mt-3">
                    Last sync: {new Date(account.lastSync).toLocaleString()}
                  </div>
                )}

                {account.syncError && (
                  <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-xs">
                    Error: {account.syncError}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Broker Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-lg shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
            <button 
                onClick={() => setShowModal(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
                ✕
            </button>
            
            {!selectedBroker ? (
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">Select Broker</h2>
                <div className="grid gap-4">
                  {supportedBrokers.map((broker) => (
                    <div
                      key={broker.id}
                      onClick={() => handleBrokerSelect(broker)}
                      className="p-4 bg-slate-700/50 hover:bg-slate-700 border border-slate-600 rounded-xl cursor-pointer transition-all hover:border-emerald-500 group"
                    >
                      <div className="text-lg font-semibold text-white mb-1 group-hover:text-emerald-400 transition-colors">
                        {broker.name}
                      </div>
                      <div className="text-sm text-slate-400">
                        {broker.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : selectedBroker.authType === "oauth" ? (
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">
                  Connect to {selectedBroker.name}
                </h2>

                <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-4 mb-6">
                  <h3 className="text-white font-medium mb-2">
                    Step 1: Configure API Credentials
                  </h3>
                  <p className="text-sm text-slate-400 mb-4">
                    Enter your {selectedBroker.name} API credentials. You'll authorize access in the next step.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {selectedBroker.fields.map((field) => (
                      <div key={field.name}>
                        <label className="block text-sm font-medium text-slate-300 mb-1">
                          {field.label}
                        </label>
                        {field.name === "redirectUri" && (
                          <span className="text-xs text-slate-500 block mb-1">
                            Use: https://127.0.0.1:3001/api/broker-accounts/schwab/callback
                            <br />
                            <span className="text-amber-500">Note: Schwab requires HTTPS for OAuth. Local HTTP will not work.</span>
                          </span>
                        )}
                        
                        {field.type === "select" ? (
                          <select
                            value={formData[field.name] || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                [field.name]: e.target.value,
                              })
                            }
                            className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-emerald-500 ring-emerald-500"
                            required={field.required}
                          >
                            {field.options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={field.type}
                            value={formData[field.name] || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                [field.name]: e.target.value,
                              })
                            }
                            className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 ring-emerald-500"
                            required={field.required}
                            placeholder={
                              field.name === "redirectUri"
                                ? "https://127.0.0.1:3001/api/broker-accounts/schwab/callback"
                                : ""
                            }
                          />
                        )}
                      </div>
                    ))}

                    <div className="flex gap-3 justify-end mt-6">
                      <button
                        type="button"
                        onClick={() => setShowModal(false)}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg font-medium transition-colors"
                      >
                        Cancel
                      </button>

                      <button type="submit" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors shadow-lg shadow-emerald-900/20">
                        Add Account & Authorize
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            ) : (
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">
                  Connect to {selectedBroker.name}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {selectedBroker.fields.map((field) => (
                    <div key={field.name}>
                        <label className="block text-sm font-medium text-slate-300 mb-1">
                          {field.label}
                        </label>
                      {field.type === "select" ? (
                        <select
                          value={formData[field.name] || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              [field.name]: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-emerald-500 ring-emerald-500"
                          required={field.required}
                        >
                          {field.options.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={field.type}
                          value={formData[field.name] || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              [field.name]: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 ring-emerald-500"
                          required={field.required}
                        />
                      )}
                    </div>
                  ))}

                  <div className="flex gap-3 justify-end mt-6">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg font-medium transition-colors"
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      onClick={handleTestConnection}
                      disabled={testingConnection}
                      className={`px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors ${testingConnection ? 'opacity-50 cursor-wait' : ''}`}
                    >
                      {testingConnection ? "Testing..." : "Test Connection"}
                    </button>

                    <button type="submit" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors shadow-lg shadow-emerald-900/20">
                      Add Account
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Brokers;
